import type { Environment } from '#app/shared/contexts/AppContext'
import { type AuthContextResult, ValueUnknown } from '#app/shared/contexts/AuthContext'
import {
  datadogRum,
  type RumEvent,
  type RumEventDomainContext,
  type RumFetchResourceEventDomainContext
} from '@datadog/browser-rum'
import { RetreiveGitInfo } from './gitInfo'
import { isStorybook } from './storybook'

interface GraphQLRequest {
  operationName?: string
  query: string
}

export const datadogRumBeforeSend = (
  event: RumEvent,
  context: RumEventDomainContext
) => {
  // collect a RUM resource's request body
  if (event.type === 'resource' && event.resource.url.endsWith('/graphql')) {
    // send the GraphQL request body as a JSON object in the context.
    const requestBody = (context as RumFetchResourceEventDomainContext)
      .requestInit?.body
    const parsedRequestBody: GraphQLRequest = requestBody
      ? JSON.parse(requestBody.toString())
      : {}
    const { query = '' } = parsedRequestBody
    const operationName = parsedRequestBody.operationName ?? 'not-defined'
    const operationType = (parsedRequestBody.operationName && query
      ? query.substring(0, query.indexOf(' '))
      : undefined) ?? 'not-defined'
    /* Now in DD, you can reference
        @context.operationName
        @context.operationType (query/mutation/not-defined)
        @context.requestBody
     */
    event.context = {
      ...event.context,
      requestBody: parsedRequestBody,
      operationName,
      operationType
    }
  }

  return true
}

// biome-ignore lint/suspicious/noExplicitAny: <explanation>
const DefaultEnableRum = !(global as any)?.IS_STORYBOOK

let enableRum = DefaultEnableRum
let initialized = false

/**
 * Initialize DataDog integration.
 * @param environment Current environment settings
 */
export function initializeDataDog(environment: Environment) {
  enableRum = DefaultEnableRum
  const version = RetreiveGitInfo().releaseName

  console.log(`App version: ${version}`)

  // Local development and E2E runs should not emit RUM to the shared tenant.
  if (environment.DD_ENV === 'local' || isStorybook()) {
    // Disable
    enableRum = false
    // eslint-disable-next-line no-console
    console.warn('Disabled DataDog integration')
  }

  if (enableRum) {
    // Ensure the tracing origin is relative to the requesting hostname.
    // Otherwise get a CORS violation with Auth0 and x-datadog-origin header.
    // Side-effect, ignore login-staging.truenorthfleet.com and login.truenorthfleet.com
    // Assume hostname ends with '.truenorthfleet.com'
    const hostname = window.location.host
    const hostPart = hostname.replace('.truenorthfleet.com', '')
    const hostPartRegex = hostPart.replace(/\./g, '\\.')
    const traceRegex = new RegExp(
      `https:\\/\\/${hostPartRegex}\\.truenorthfleet\\.com`
    )

    // Initialize DataDog RUM integration
    datadogRum.init({
      applicationId: 'e076eba0-b578-4b01-98d9-b969cb1b6b5e',
      clientToken: 'pubfa2b9c98f9dc7f0081dd4c29afce415f',
      site: 'us3.datadoghq.com',
      service: 'portal-broker',
      env: environment.DD_ENV,
      version,
      sessionReplaySampleRate: 100,
      trackUserInteractions: true,
      allowedTracingUrls: [traceRegex],
      defaultPrivacyLevel: 'mask-user-input',
      enableExperimentalFeatures: ['clickmap', 'feature_flags'],
      beforeSend: datadogRumBeforeSend
    })

    // enable session recording
    datadogRum.startSessionReplayRecording()
  }
  initialized = true
}

export const datadogSetUser = (authContext: AuthContextResult | null) => {
  if (!enableRum) {
    return
  }

  if (!authContext?.authenticated) {
    datadogRum.clearUser()
    return
  }

  const { userKey, userFullName, userEmail } = authContext
  const custom = { ...authContext.externalContext }

  // set the authenticated user with DataDog RUM
  datadogRum.setUser({
    id: userKey,
    name: userFullName ?? ValueUnknown,
    email: userEmail ?? ValueUnknown,
    ...custom
  })
}

// biome-ignore lint/suspicious/noExplicitAny: <explanation>
export const datadogLogError = (error: Error, context?: any) => {
  if (!enableRum) {
    console.error({ error, context })
    return
  }

  if (!initialized) {
    console.error('DataDog integration not initialized.')
  }

  datadogRum.addError(error, context)
}

// exported for testability
export const resetDataDog = () => {
  initialized = false
  enableRum = DefaultEnableRum
}
